import React from 'react';
import styled from 'styled-components';
import { SignUp } from '@clerk/clerk-react';
import { motion } from 'framer-motion';

const SignUpContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  position: relative;
  padding-top: 120px; /* Platz für Navbar */
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(243, 156, 18, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(243, 156, 18, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SignUpContent = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 240px); /* Vollständige Zentrierung */
`;

const SignUpCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #f39c12, #e67e22, #f1c40f, #f4d03f);
    border-radius: 24px 24px 0 0;
  }
`;

const SignUpHeader = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
  width: 100%;
`;

const SignUpTitle = styled.h1`
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 2.5rem;
  margin: 0 auto 1rem auto;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  width: fit-content;
`;

const SignUpSubtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.1rem;
  margin: 0 auto;
  font-weight: 500;
  text-align: center;
  max-width: 300px;
  line-height: 1.5;
`;

const DisabledMessage = styled(motion.div)`
  background: linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%);
  border: 1px solid rgba(243, 156, 18, 0.2);
  border-radius: 20px;
  padding: 2.5rem;
  text-align: center;
  margin-bottom: 2.5rem;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(243, 156, 18, 0.05) 0%, rgba(230, 126, 34, 0.05) 100%);
    pointer-events: none;
  }
`;

const DisabledIcon = styled(motion.div)`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 4px 8px rgba(243, 156, 18, 0.3));
`;

const DisabledTitle = styled.h2`
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 1.5rem 0;
  font-size: 1.8rem;
  font-weight: 700;
`;

const DisabledText = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin: 0 0 2rem 0;
  line-height: 1.6;
  font-size: 1rem;
  font-weight: 500;
`;

const BackToLogin = styled.div`
  text-align: center;
  width: 100%;
`;

const BackLink = styled.a`
  color: ${props => props.theme.colors.textLight};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  background: rgba(243, 156, 18, 0.1);
  border: 1px solid rgba(243, 156, 18, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
  margin: 0 auto;
  
  &:hover {
    color: white;
    background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(243, 156, 18, 0.3);
  }
`;

const AdminSignUp = () => {
  // Sign-up ist deaktiviert - zeige entsprechende Nachricht
  return (
    <SignUpContainer>
      <SignUpContent>
        <SignUpCard
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <SignUpHeader>
            <SignUpTitle>Admin Registrierung</SignUpTitle>
            <SignUpSubtitle>
              Neue Admin-Accounts können nur von bestehenden Administratoren erstellt werden
            </SignUpSubtitle>
          </SignUpHeader>
          
          <DisabledMessage
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <DisabledIcon
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4, type: "spring", stiffness: 200 }}
            >
              🚫
            </DisabledIcon>
            <DisabledTitle>Registrierung deaktiviert</DisabledTitle>
            <DisabledText>
              Die Selbstregistrierung für Admin-Accounts ist aus Sicherheitsgründen deaktiviert. 
              Wenn Sie Administrator-Zugriff benötigen, wenden Sie sich an einen bestehenden Administrator.
            </DisabledText>
          </DisabledMessage>
          
          <BackToLogin>
            <BackLink href="/admin">← Zurück zum Login</BackLink>
          </BackToLogin>
        </SignUpCard>
      </SignUpContent>
    </SignUpContainer>
  );
};

export default AdminSignUp;
